import _ from "lodash";
import React, { useCallback, useRef, useState } from "react";
import moment from "moment";
import PropTypes from "prop-types";
import classNames from "classnames";
import { Modal, Button, RadioButton, EmptyState } from "@toddle-edu/ds-web";
import { NoMessagesIllustration } from "@toddle-edu/ds-theme";

import { useHandleScroll } from "Utils/CustomHooks";
import { graphql } from "lib/apolloClient";
import { getOrganizationId } from "modules/CommonSelectors";
import prepareLoadMoreFeed from "Utils/PrepareLoadMoreFeedHOF";
import { useChatOptions } from "MessagingV2/providers/ChatOptionsProvider";
import withLoader from "UIComponents/GqlWithLoader";
import Loading from "UIComponents/Loading";
import {
  PAGINATION_LIMITS,
  MESSAGE_TYPE,
  CHAT_TYPE_BE,
  CHAT_TYPE,
  getMessageDOMId,
  getIsPreviewChatInSummaryModalEnabled,
  isMessagingGlobalHeaderRevampEnable,
} from "MessagingV2/modules/Utils";
import { useI18n } from "Hooks";
import Messages from "MessagingV2/components/Messages";
import { getChatMessagesForPreviewQuery } from "MessagingV2/modules/MessagingQuery";

import classes from "./SummaryInnerModal.scss";

const DURATION = {
  LAST_WEEK: "LAST_WEEK",
  TODAY: "TODAY",
};

const styles = {
  modalBodyStyle: { paddingBlock: "8px" },
  nextButtonStyle: { height: "48px", padding: "12px 16px" },
};

const getRangeBasedOnDuration = duration => {
  const now = moment();

  if (duration === DURATION.LAST_WEEK) {
    const after = moment(now)
      .subtract(1, "week")
      .add(1, "day")
      .startOf("day")
      .toISOString(true);
    const before = moment(now).endOf("day").toISOString(true);
    return { after, before };
  }

  const after = moment(now).startOf("day").toISOString(true);
  const before = moment(now).endOf("day").toISOString(true);
  return { after, before };
};

const MessagesPreview = props => {
  const {
    type,
    lastReadMessage,
    networkStatus,
    hasNextPage,
    messagesEdges,
    loadMoreMessages,
  } = props;
  const containerRef = useRef();
  const { t } = useI18n(["common"]);

  const chatType =
    type === CHAT_TYPE_BE.CHAT_PERSONAL ? CHAT_TYPE.CHAT : CHAT_TYPE.CHANNEL;
  const isLoadingMore = networkStatus === 3;

  useHandleScroll({
    hasNextPage,
    networkStatus,
    loadMoreFeed: loadMoreMessages,
    getScrollRef: () => containerRef.current,
  });

  const getMessageElementIdCb = useCallback(message => {
    const id = getMessageDOMId(message);
    return `Preview-modal-${id}`;
  }, []);

  if (_.isEmpty(messagesEdges)) {
    return (
      <div className={classes.emptyContainer}>
        <div className={classes.emptyInnerContainer}>
          <EmptyState
            size="small"
            illustration={NoMessagesIllustration}
            subtitle={t(
              "common:no_messages_to_summarise_in_the_selected_range"
            )}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={classes.scrollableContainer} ref={containerRef}>
      <div className={classes.emptySpace} />
      <Messages
        chatType={chatType}
        messages={messagesEdges}
        lastReadMessage={lastReadMessage}
        onEditMessageClick={_.noop}
        onRetryMessageClick={_.noop}
        onReplyMessageClick={_.noop}
        onParentMessageClick={_.noop}
        highlightedMessageId={null}
        isMessageOptionsVisible={false}
        unreadMessageCount={0}
        showUnreadLine={false}
        getMessageElementIdCb={getMessageElementIdCb}
      />
      {isLoadingMore ? (
        <div className={classes.loaderContainer}>
          <Loading />
        </div>
      ) : null}
    </div>
  );
};

const MessagesPreviewWithLoader = withLoader(MessagesPreview);

const SummaryInnerModal = props => {
  const {
    onCloseModal,
    onApplyDuration,
    selectedDuration,
    setSelectedDuration,
    type,
    lastReadMessage,
    networkStatus,
    hasNextPage,
    messagesEdges,
    loadMoreMessages,
    isLoading,
    isData,
  } = props;

  const { t } = useI18n(["common"]);
  const isPreviewChatEnabled = getIsPreviewChatInSummaryModalEnabled();
  const isSubmitDisabled = isPreviewChatEnabled && _.isEmpty(messagesEdges);

  const radioOptions = [
    {
      key: DURATION.TODAY,
      label: t("common:today"),
    },
    {
      key: DURATION.LAST_WEEK,
      label: t("common:last_7_days"),
    },
  ];

  const handleRadioChange = event => {
    setSelectedDuration(event.target.value);
  };

  const onSubmit = () => {
    if (isSubmitDisabled) return;
    const { after, before } = getRangeBasedOnDuration(selectedDuration);
    onApplyDuration({ after, before });
  };

  return (
    <Modal width="480px" height="fit-content" isOpen onClose={onCloseModal}>
      <Modal.Header title={t("common:select_duration")} isClosable />
      <Modal.Body style={styles.modalBodyStyle}>
        <div className={classes.content}>
          {_.map(radioOptions, option => {
            const { key, label } = option;
            return (
              <RadioButton
                size="xx-small"
                key={key}
                value={key}
                checked={key === selectedDuration}
                onChange={handleRadioChange}
              >
                <div className={classes.radioText}>{label}</div>
              </RadioButton>
            );
          })}
          {isPreviewChatEnabled ? (
            <div className={classes.previewContentContainer}>
              <div
                className={classNames(
                  "text-label-3",
                  "text-textSubtle",
                  classes.previewText
                )}
              >
                {t("common:preview")}
              </div>
              <div className={classes.messagesContainer}>
                <MessagesPreviewWithLoader
                  duration={selectedDuration}
                  type={type}
                  lastReadMessage={lastReadMessage}
                  networkStatus={networkStatus}
                  hasNextPage={hasNextPage}
                  messagesEdges={messagesEdges}
                  loadMoreMessages={loadMoreMessages}
                  isLoading={isLoading}
                  isData={isData}
                />
              </div>
            </div>
          ) : null}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className={classes.footerWrapper}>
          <Button size="large" variant="outlined" onClick={onCloseModal}>
            {t("common:cancel")}
          </Button>
          <Button
            size="large"
            variant="progressive"
            disabled={isSubmitDisabled}
            onClick={onSubmit}
            {...(isMessagingGlobalHeaderRevampEnable()
              ? {
                  styles: styles.nextButtonStyle,
                  dsVersion: "2.0",
                  shortcut: { code: "Enter", shiftKey: true },
                }
              : {})}
          >
            {t("common:summarise")}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

SummaryInnerModal.propTypes = {
  onCloseModal: PropTypes.func.isRequired,
  onApplyDuration: PropTypes.func.isRequired,
};

const getMessagesMemoize = _.memoize(messagesEdges =>
  _.reject(messagesEdges, ({ node }) => node.isDeleted)
);

const SummaryInnerModalWithData = graphql(getChatMessagesForPreviewQuery, {
  name: "getChatMessagesForPreviewQuery",
  skip: () => !getIsPreviewChatInSummaryModalEnabled(),
  options: ({ selectedDuration, chatId }) => {
    const { after } = getRangeBasedOnDuration(selectedDuration);
    return {
      fetchPolicy: "cache-and-network",
      variables: {
        id: chatId,
        organizationId: getOrganizationId(),
        input: {
          dateFrom: after, // only dateFrom is needed as beginning point
          first: PAGINATION_LIMITS.MESSAGES_FEED,
          messageType: [MESSAGE_TYPE.NORMAL],
          orderByDirection: "ASC",
        },
      },
    };
  },
  props: ({ getChatMessagesForPreviewQuery }) => {
    const { networkStatus, variables, node } = getChatMessagesForPreviewQuery;
    const { type, lastReadMessage } = node ?? {};
    const messagesEdges = getMessagesMemoize(
      _.get(node, "messagesV2.edges", [])
    );

    const { hasNextPage, endCursor } = _.get(node, "messagesV2.pageInfo", {});
    const isLoading = _.includes([1, 2], networkStatus);

    // Basically show loader every time in cache-and-network if
    // the list is empty, to avoid flickering(from empty state to date state)
    const isData =
      !_.isEmpty(node) && (!_.isEmpty(messagesEdges) || !isLoading);

    return {
      isLoading,
      isData,
      type,
      lastReadMessage,
      messagesEdges,
      hasNextPage,
      networkStatus,
      loadMoreMessages: prepareLoadMoreFeed({
        variables: {
          ...variables,
          input: { ...variables.input, before: endCursor },
        },
        queryData: getChatMessagesForPreviewQuery,
        key: "messagesV2",
      }),
    };
  },
})(SummaryInnerModal);

const ModalWrapper = props => {
  const { chatId } = useChatOptions();
  const [selectedDuration, setSelectedDuration] = useState(DURATION.TODAY);

  return (
    <SummaryInnerModalWithData
      {...props}
      chatId={chatId}
      selectedDuration={selectedDuration}
      setSelectedDuration={setSelectedDuration}
    />
  );
};

export default ModalWrapper;
